<template>
  <section
    ref="rootRef"
    class="arc-focus-carousel"
    :class="[
      `arc-focus-carousel--${background}`,
      { 'arc-focus-carousel--loading': loading },
    ]"
    :style="{ '--afc-entry-duration': `${ENTRY_DURATION_MS}ms` }"
    data-test-id="arc-focus-carousel"
    role="group"
    aria-roledescription="carrossel"
    aria-label="Lista de cartões"
    :aria-busy="loading || undefined"
  >
    <div class="arc-focus-carousel__stage">
      <div
        class="arc-focus-carousel__track"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
      >
        <div
          class="arc-focus-carousel__wheel"
          role="list"
          :style="{ transform: `rotate(${-position * ANGLE_STEP}deg)` }"
        >
          <div
            v-for="slot in slots"
            :key="slot.key"
            :ref="(el) => registerSlot(slot.key, el)"
            class="arc-focus-carousel__slot"
            :class="{ 'is-active': slot.offset === 0 }"
            :style="slotStyle(slot)"
            role="listitem"
            :tabindex="slot.offset === 0 ? 0 : -1"
            :aria-hidden="slot.hidden ? 'true' : undefined"
            :aria-current="slot.offset === 0 ? 'true' : undefined"
            :aria-label="cardLabel(slot)"
            :data-test-id="`arc-focus-carousel-card-${slot.key}`"
            @click="onCardClick(slot)"
            @keydown="onCardKeydown($event, slot)"
          >
            <div
              class="arc-focus-carousel__card"
              :class="{ 'gradient-loading': loading }"
            >
              <slot
                v-if="!loading"
                name="card"
                :card="slot.card"
                :index="slot.cardIndex"
                :active="slot.offset === 0"
              >
                <img
                  v-if="slot.card.imageUrl"
                  :src="slot.card.imageUrl"
                  :alt="slot.card.imageAlt"
                  aria-hidden="true"
                  class="arc-focus-carousel__img"
                  draggable="false"
                />
              </slot>
            </div>
          </div>
        </div>
      </div>

      <div class="arc-focus-carousel__nav gap-size-4">
        <button
          type="button"
          class="arc-focus-carousel__arrow"
          :class="
            loading ? 'gradient-loading' : 'bg-theme-primary text-text-inverted'
          "
          :disabled="!canNavigate"
          data-test-id="arc-focus-carousel-prev"
          aria-label="Cartão anterior"
          @click="prev"
        >
          <IconComponent
            v-if="!loading"
            name="chev_left"
            size="2"
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          class="arc-focus-carousel__arrow"
          :class="
            loading ? 'gradient-loading' : 'bg-theme-primary text-text-inverted'
          "
          :disabled="!canNavigate"
          data-test-id="arc-focus-carousel-next"
          aria-label="Cartão seguinte"
          @click="next"
        >
          <IconComponent
            v-if="!loading"
            name="chev_right"
            size="2"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>

    <!-- Cantos inferiores: escondem as pontas dos cartões exteriores que descem
         para fora do arco, desvanecendo-as para o fundo do módulo a descer e a
         caminho do centro. Só existem a partir de 1024px, onde esses cartões
         chegam a ser visíveis. -->
    <div
      aria-hidden="true"
      data-test-id="fade-left"
      class="arc-focus-carousel__fade arc-focus-carousel__fade--left"
    />
    <div
      aria-hidden="true"
      data-test-id="fade-right"
      class="arc-focus-carousel__fade arc-focus-carousel__fade--right"
    />

    <div
      :key="loading ? 'loading' : activeIndex"
      class="arc-focus-carousel__message gap-size-7"
    >
      <template v-if="loading">
        <div class="arc-focus-carousel__message-block gap-size-3">
          <div class="arc-focus-carousel__skeleton-title gradient-loading" />
          <div class="arc-focus-carousel__skeleton-text gradient-loading" />
        </div>
        <div class="arc-focus-carousel__skeleton-button gradient-loading" />
      </template>

      <template v-else-if="messageCard">
        <div class="arc-focus-carousel__message-block gap-size-3">
          <component
            v-if="messageCard.title"
            :is="normalizeHeadingTag(messageCard.titleHeadingType, 'p')"
            data-test-id="arc-focus-carousel-title"
            :class="['text-text-headers', titleTokenClass]"
          >
            {{ messageCard.title }}
          </component>
          <component
            v-if="messageCard.description"
            :is="normalizeHeadingTag(messageCard.descriptionHeadingType, 'p')"
            data-test-id="arc-focus-carousel-description"
            class="text-text-body-variant paragraph-small"
          >
            {{ messageCard.description }}
          </component>
        </div>

        <component
          :is="messageCard.ctaUrl ? 'a' : 'div'"
          v-if="messageCard.ctaText"
          class="arc-focus-carousel__cta"
          :href="messageCard.ctaUrl || undefined"
          :target="messageCard.ctaTarget || undefined"
          :rel="ctaRel"
        >
          <BaseButton
            class="theme primary"
            data-test-id="arc-focus-carousel-cta"
            :label="messageCard.ctaText"
            @click="onCta"
          />
        </component>
      </template>
    </div>

    <p class="arc-focus-carousel__sr-only" aria-live="polite">
      {{ announcement }}
    </p>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { BaseButton, IconComponent } from "ca-online-design-system";
import { normalizeHeadingTag } from "../../../utils/heading";

/**
 * ArcFocusCarousel — o "Arco de Cartões". Sete cartões assentam numa única
 * circunferência cujo centro fica muito abaixo do palco; navegar roda a
 * circunferência inteira de um passo, e o cartão que fica a 0° é o selecionado,
 * alimentando o Module Message (label/título/descrição/CTA) por baixo.
 *
 * Modelo de movimento (do protótipo de animação do Figma Make):
 *  - Os cartões NÃO se movem uns em relação aos outros: cada um tem um ângulo
 *    fixo na roda e é a roda que gira. Assim viajam ao longo do arco em vez de
 *    cortarem caminho em linha reta, que é o que aconteceria se cada cartão
 *    animasse a sua própria posição.
 *  - Cada posição da roda tem um índice virtual que cresce sem limite. Quando o
 *    arco roda, os cartões que saem do campo de visão são desmontados e outros
 *    montam do lado oposto — sempre fora do ecrã, para lá dos ±53,7° que o
 *    protótipo usa como fronteira invisível. Nunca se vê um cartão a saltar de
 *    lado, e o número de cartões não tem de encher o arco: com menos de sete, o
 *    mesmo cartão reaparece noutra posição.
 *  - 220ms, easing `ease` (0.25, 0.1, 0.25, 1) — os dois do protótipo.
 *  - O Module Message troca de conteúdo com o cartão e entra a fazer-se em
 *    160ms, animação que arranca sozinha por o bloco ser recriado.
 *  - Entrada (fan-out): quando o módulo aparece no viewport pela primeira vez,
 *    o leque abre a partir do cartão ativo em 1,5s. Corre uma única vez por
 *    carregamento da página — navegar no carrossel não a repete.
 *
 * O fundo é do módulo e vem da prop `background` — branco ou solarized, os dois
 * fundos que a DR pediu. Os gradientes que desvanecem os cantos inferiores usam
 * a mesma cor, pelo que têm de a conhecer: é por isso que a cor é prop e não
 * herdada da div que envolve o módulo.
 *
 * Os cartões não têm estado de hover: a única resposta visual à navegação é a
 * rotação do arco. Clicar em qualquer cartão traz-no ao centro numa só rotação.
 */
defineOptions({
  name: "ArcFocusCarousel",
});

export interface ArcFocusCarouselCard {
  /** Url da arte do cartão. */
  imageUrl?: string;
  /** Nome acessível do cartão, ex.: `"Cartão VISA Business"`. A arte em si é
   * anunciada como decorativa — é este texto que dá nome ao cartão. */
  imageAlt?: string;
  /** Título mostrado no Module Message. */
  title?: string;
  titleHeadingType?: string;
  /** Descrição, por baixo do título. */
  description?: string;
  descriptionHeadingType?: string;
  /** Texto do call to action. Vazio = sem botão. */
  ctaText?: string;
  /** Url do call to action. Vazia = botão sem navegação. */
  ctaUrl?: string;
  /** Onde abrir a url, ex.: `"_blank"`. Vazio = mesmo separador. */
  ctaTarget?: string;
}

interface Props {
  /** Cartões a apresentar. Mínimo recomendado pelo design: 5. */
  cards?: ArcFocusCarouselCard[];
  /** Enquanto true o módulo mantém a forma mas mostra shimmer em vez dos
   * cartões, dos controlos e da mensagem. */
  loading?: boolean;
  /** Fundo do módulo: `"white"` (#fff) ou `"solarized"` (#f6f7f2). É também a
   * cor para onde os cantos inferiores desvanecem. */
  background?: "white" | "solarized";
}

const props = withDefaults(defineProps<Props>(), {
  cards: () => [],
  loading: false,
  background: "solarized",
});

const emit = defineEmits<{
  (e: "change", index: number): void;
  (e: "cta-click", card: ArcFocusCarouselCard, index: number): void;
  (e: "card-click", card: ArcFocusCarouselCard, index: number): void;
}>();

/** Graus entre posições adjacentes do arco. */
const ANGLE_STEP = 15.33;
/** Cartões de cada lado do central: ±15,33° / ±30,67° / ±46°. */
const VISIBLE_PER_SIDE = 3;

const count = computed(() => props.cards.length);

/** Enquanto carrega não há conteúdo, mas o arco tem de manter a forma. */
const skeletonCards = computed<ArcFocusCarouselCard[]>(() =>
  Array.from({ length: VISIBLE_PER_SIDE * 2 + 1 }, () => ({})),
);

const wheelCards = computed(() =>
  props.loading ? skeletonCards.value : props.cards,
);

/** Com um único cartão não há nada para rodar. */
const canNavigate = computed(
  () => !props.loading && wheelCards.value.length > 1,
);

/**
 * Posição da roda: o índice virtual que está a 0°. Cresce sem limite — é isso
 * que faz a rotação ser sempre contínua, sem a volta ao início ter de saltar
 * para trás.
 */
const position = ref(0);

/** Índice real do cartão ativo. */
const activeIndex = computed(() => wrapIndex(position.value));

function wrapIndex(virtualIndex: number) {
  const n = wheelCards.value.length;
  if (n === 0) return 0;
  return ((virtualIndex % n) + n) % n;
}

/**
 * Janela de cartões montados: um passo além dos visíveis de cada lado. Os
 * cartões desse passo extra estão para lá da fronteira invisível do arco, pelo
 * que montam e desmontam sem se verem.
 */
const slots = computed(() => {
  if (wheelCards.value.length === 0) return [];

  const reach = VISIBLE_PER_SIDE + 1;
  const out = [];
  for (let key = position.value - reach; key <= position.value + reach; key++) {
    const cardIndex = wrapIndex(key);
    const offset = key - position.value;
    out.push({
      key,
      cardIndex,
      card: wheelCards.value[cardIndex],
      offset,
      hidden: Math.abs(offset) > VISIBLE_PER_SIDE,
    });
  }
  return out;
});

/**
 * Ângulo absoluto do cartão na roda. Depois da entrada nunca muda enquanto o
 * cartão está montado — quem se move é a roda.
 *
 * Antes da entrada, os cartões que não são o central estão empilhados no
 * centro e invisíveis: é daí que o leque se abre.
 */
const slotStyle = (slot: { key: number; offset: number; hidden: boolean }) => {
  const distance = Math.abs(slot.offset);
  const stacked = entry.value === "waiting" && distance !== 0;
  return {
    transform: `rotate(${(stacked ? position.value : slot.key) * ANGLE_STEP}deg)`,
    // Ordem do protótipo: centro por cima, e os de fora sempre por baixo dos
    // gradientes (z-index 20) e dos controlos (z-index 30).
    zIndex: `${distance === 0 ? 10 : distance === 1 ? 7 : distance === 2 ? 5 : 3}`,
    opacity: slot.hidden || stacked ? "0" : undefined,
    pointerEvents: slot.hidden || stacked ? "none" : undefined,
    // O leque abre de dentro para fora. O atraso só existe durante a entrada:
    // depois dela, deixá-lo cá arrastaria também o fade dos cartões que entram
    // e saem do arco na navegação.
    transitionDelay:
      entry.value === "running"
        ? `${distance * ENTRY_STAGGER_MS}ms`
        : undefined,
  } as Record<string, string | undefined>;
};

// --- animação de entrada (fan-out) ---------------------------------------
// Começa com o cartão ativo sozinho no centro e abre o leque à medida que o
// módulo entra no viewport. Corre uma única vez por carregamento da página: a
// navegação no carrossel não a repete, e `entry` nunca volta a "waiting".
// 900 de percurso + 3 x 200 de desfasamento = 1,5s do primeiro cartão até o
// leque assentar. A duração vai para o CSS como custom property (ver o `style`
// na raiz do template), para não haver dois sítios a dizer quanto demora.
const ENTRY_DURATION_MS = 900;
const ENTRY_STAGGER_MS = 200;
const ENTRY_TOTAL_MS = ENTRY_DURATION_MS + VISIBLE_PER_SIDE * ENTRY_STAGGER_MS;

/** Quanto do fundo do viewport não conta para o arranque. A 45%, o leque só
 * abre depois de o topo do módulo passar a linha dos 55% do ecrã — a meio da
 * animação o arco está perto do centro, que é onde se quer ver. */
const ENTRY_VIEWPORT_CUT = "45%";

const entry = ref<"waiting" | "running" | "done">("waiting");
const rootRef = ref<HTMLElement | null>(null);
let entryObservers: IntersectionObserver[] = [];
let entryTimer: ReturnType<typeof setTimeout> | undefined;

function stopObservingEntry() {
  entryObservers.forEach((observer) => observer.disconnect());
  entryObservers = [];
}

function startEntry() {
  if (entry.value !== "waiting") return;
  stopObservingEntry();

  // Dois frames antes de abrir: garante que o browser chegou a pintar os
  // cartões empilhados no centro. Trocar o estado no mesmo frame em que eles
  // aparecem faria a transição não arrancar e o leque surgir já aberto.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      entry.value = "running";
      // Só limpa os atrasos escalonados quando o leque acabou de abrir. Um
      // atraso neste temporizador não se vê: a animação já terminou por CSS.
      entryTimer = setTimeout(() => {
        entry.value = "done";
      }, ENTRY_TOTAL_MS);
    });
  });
}

onMounted(() => {
  // Sem IntersectionObserver não há forma de saber quando o módulo aparece —
  // mais vale o leque aberto do que os cartões empilhados para sempre.
  if (typeof IntersectionObserver === "undefined") {
    entry.value = "done";
    return;
  }
  const root = rootRef.value;
  if (!root) return;

  const onIntersect = (entries: IntersectionObserverEntry[]) => {
    if (entries.some((e) => e.isIntersecting)) startEntry();
  };

  entryObservers = [
    // O gatilho normal: o módulo tem de subir até meio do ecrã.
    new IntersectionObserver(onIntersect, {
      rootMargin: `0px 0px -${ENTRY_VIEWPORT_CUT} 0px`,
      threshold: 0,
    }),
    // Rede de segurança: se o módulo estiver no fim da página, pode não haver
    // scroll que chegue para o topo dele passar aquela linha. Aqui basta estar
    // quase todo à vista — sem isto os cartões ficavam empilhados para sempre.
    new IntersectionObserver(onIntersect, { threshold: 0.6 }),
  ];
  entryObservers.forEach((observer) => observer.observe(root));
});

/* Token tipográfico do título do Module Message: heading-jumbo até 1024px
   inclusive, heading-large daí para cima.

   A troca é feita aqui e não em CSS porque heading-jumbo e heading-large são
   classes já compiladas na folha do design system, não utilitários do Tailwind
   local (ver a nota no topo do <style>). Alterná-las em CSS obrigava a copiar
   os valores dos dois tokens para dentro deste ficheiro, e essa cópia deixava
   de acompanhar a escala do DS no dia em que ela mudasse. Assim o DS continua a
   ser quem define cada token e este componente só escolhe qual deles usar.

   1024.02px em vez de 1025px pela mesma razão que a grelha usa o passo de
   0,02px: deixa os 1024 exatos do lado do jumbo mesmo com larguras
   fracionárias, que é o que aparece com zoom ou em ecrãs HiDPI. */
const TITLE_TOKEN_QUERY = "(min-width: 1024.02px)";
const titleQuery =
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia(TITLE_TOKEN_QUERY)
    : undefined;
/* Lido já aqui, e não só no onMounted, para o título não nascer com o token
   errado e trocar à frente do utilizador. */
const aboveTitleBreakpoint = ref(titleQuery?.matches ?? false);
const titleTokenClass = computed(() =>
  aboveTitleBreakpoint.value ? "heading-large" : "heading-jumbo",
);

function onTitleQueryChange(event: MediaQueryListEvent) {
  aboveTitleBreakpoint.value = event.matches;
}

onMounted(() => {
  if (!titleQuery) return;
  aboveTitleBreakpoint.value = titleQuery.matches;
  titleQuery.addEventListener("change", onTitleQueryChange);
});

onBeforeUnmount(() => {
  stopObservingEntry();
  clearTimeout(entryTimer);
  titleQuery?.removeEventListener("change", onTitleQueryChange);
});

/** "Cartão 3 de 7. Cartão VISA Business." — o nome pedido nas specs de a11y. */
const cardLabel = (slot: { cardIndex: number; card: ArcFocusCarouselCard }) => {
  if (props.loading || !count.value) return undefined;
  const name = slot.card.imageAlt || slot.card.title;
  const base = `Cartão ${slot.cardIndex + 1} de ${count.value}`;
  return name ? `${base}. ${name}` : base;
};

// --- Module Message ------------------------------------------------------
// O conteúdo é sempre o do cartão ativo. A entrada é uma animação CSS que
// arranca sozinha por o bloco ser recriado (ver a `key` no template): não há
// temporizador a marcar o meio da rotação, que era o que fazia o texto demorar
// a aparecer sempre que o browser estava ocupado a montar cartões — e ficar
// apagado durante uma rajada de cliques, porque cada clique o reiniciava.
const messageCard = computed<ArcFocusCarouselCard | undefined>(
  () => props.cards[activeIndex.value],
);

const ctaRel = computed(() =>
  messageCard.value?.ctaTarget === "_blank" ? "noopener noreferrer" : undefined,
);

// --- foco e anúncios -----------------------------------------------------
// Roving tabindex: só o cartão ativo é um tab stop; as setas do teclado movem a
// seleção e o foco acompanha o cartão que passa a ativo. Navegar pelos botões
// não move o foco — quem está nas setas fica nas setas.
const slotEls = new Map<number, HTMLElement>();
const announcement = ref("");

const registerSlot = (key: number, el: unknown) => {
  if (el instanceof HTMLElement) slotEls.set(key, el);
  else slotEls.delete(key);
};

function focusActiveCard() {
  nextTick(() => slotEls.get(position.value)?.focus());
}

function announceActive() {
  if (!count.value) return;
  const card = props.cards[activeIndex.value];
  const name = card?.imageAlt || card?.title;
  const base = `Cartão ${activeIndex.value + 1} de ${count.value}`;
  announcement.value = name ? `${base}. ${name}` : base;
}

/** Roda o arco `delta` posições. Uma só rotação, por maior que seja o salto. */
function rotate(delta: number, options: { focusCard?: boolean } = {}) {
  if (delta === 0 || !canNavigate.value) return;

  position.value += delta;
  emit("change", activeIndex.value);

  announceActive();
  if (options.focusCard) focusActiveCard();
}

/** Vai para um índice real, pelo caminho mais curto. */
function goToIndex(index: number, options: { focusCard?: boolean } = {}) {
  const n = wheelCards.value.length;
  if (n === 0) return;
  let delta = (((index - activeIndex.value) % n) + n) % n;
  if (delta > n / 2) delta -= n;
  rotate(delta, options);
}

function next() {
  rotate(1);
}

function prev() {
  rotate(-1);
}

// Qualquer cartão vem para o centro numa rotação só; o que já lá está reemite o
// clique para o consumidor reagir (ex.: abrir detalhe).
function onCardClick(slot: {
  offset: number;
  card: ArcFocusCarouselCard;
  cardIndex: number;
}) {
  if (props.loading) return;
  if (dragMoved) {
    dragMoved = false;
    return;
  }
  if (slot.offset === 0) emit("card-click", slot.card, slot.cardIndex);
  else rotate(slot.offset);
}

function onCardKeydown(
  event: KeyboardEvent,
  slot: { offset: number; card: ArcFocusCarouselCard; cardIndex: number },
) {
  if (props.loading) return;

  switch (event.key) {
    case "ArrowLeft":
      event.preventDefault();
      rotate(-1, { focusCard: true });
      break;
    case "ArrowRight":
      event.preventDefault();
      rotate(1, { focusCard: true });
      break;
    case "Home":
      event.preventDefault();
      goToIndex(0, { focusCard: true });
      break;
    case "End":
      event.preventDefault();
      goToIndex(count.value - 1, { focusCard: true });
      break;
    case "Enter":
    case " ":
    case "Spacebar":
      event.preventDefault();
      onCardClick(slot);
      break;
    default:
      break;
  }
}

function onCta() {
  const card = messageCard.value;
  if (card) emit("cta-click", card, activeIndex.value);
}

// --- drag / swipe -------------------------------------------------------
// O swipe é um extra: não substitui nem bloqueia o teclado nem os controlos.
//
// A captura do ponteiro só é pedida DEPOIS de o gesto se revelar um arrasto.
// Com captura ativa o browser dispara o `click` no elemento que capturou, e não
// no cartão — capturar logo no pointerdown tornava os cartões não-clicáveis.
const SWIPE_THRESHOLD = 40;
const MOVE_TOLERANCE = 8;
let pointerActive = false;
let pointerStartX = 0;
let dragMoved = false;

function onPointerDown(event: PointerEvent) {
  if (props.loading) return;
  pointerActive = true;
  dragMoved = false;
  pointerStartX = event.clientX;
}

function onPointerMove(event: PointerEvent) {
  if (!pointerActive || dragMoved) return;
  if (Math.abs(event.clientX - pointerStartX) > MOVE_TOLERANCE) {
    dragMoved = true;
    // A partir daqui o click já não interessa (é engolido em onCardClick), e a
    // captura garante que o fim do arrasto chega mesmo que o dedo saia do arco.
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  }
}

function onPointerUp(event: PointerEvent) {
  if (!pointerActive) return;
  pointerActive = false;
  const delta = event.clientX - pointerStartX;
  if (Math.abs(delta) > SWIPE_THRESHOLD) rotate(delta < 0 ? 1 : -1);
}

defineExpose({ next, prev, goToIndex });
</script>

<style lang="postcss" scoped>
/* As classes de token do DS (gap-size-*, bg-theme-primary, heading-jumbo,
   gradient-loading) vivem no template e não em @apply: quem as fornece é a
   folha compilada do design system — o Tailwind local desta app não conhece
   esses tokens. */

.arc-focus-carousel {
  /* Geometria do arco, do protótipo de animação: os sete cartões assentam numa
     circunferência com centro muito abaixo do palco.

     O protótipo tinha cartões de 240x380 numa circunferência de raio 1143,65
     centrada em (1079, 1369.79) do espaço de 2158. Abaixo de Desktop Lg a DR
     pediu 182x288, e 182/240 e 288/380 dão o mesmo fator — 0,7583 —, por isso
     o que se reduz é o leque inteiro e não só o cartão: o raio acompanha a
     largura do cartão, senão as folgas entre cartões cresciam e o leque abria
     mais do que no desenho.

     Daí `--afc-scale` ser o único número a mexer entre breakpoints: tudo o que
     é comprimento sai do valor do protótipo multiplicado por ele. A partir de
     1366 volta a 1, ou seja, aos 240x380 de origem. O ângulo entre cartões
     (ANGLE_STEP, no script) é adimensional e fica de fora desta conta. */
  --afc-scale: 0.7583;
  --afc-arc-width: calc(2158px * var(--afc-scale));
  --afc-arc-height: calc(534px * var(--afc-scale));
  --afc-card-width: calc(240px * var(--afc-scale));
  --afc-card-height: calc(380px * var(--afc-scale));
  --afc-card-top: calc(36.14px * var(--afc-scale));
  /* Centro da circunferência, medido a partir do topo da roda: topo do cartão
     + meia altura + raio (36,14 + 190 + 1143,65). O pivô de cada cartão é o
     mesmo ponto, medido a partir do topo do cartão. */
  --afc-wheel-pivot: calc(1369.79px * var(--afc-scale));
  --afc-card-pivot: calc(var(--afc-wheel-pivot) - var(--afc-card-top));
  --afc-card-radius: calc(13.846px * var(--afc-scale));
  /* Opacidade da arte dos cartões que não estão no centro. */
  --afc-inactive-opacity: 0.4;
  /* Rotação do arco. */
  --afc-rotation-duration: 220ms;
  --afc-rotation-ease: cubic-bezier(0.25, 0.1, 0.25, 1);
  /* Entrada do Module Message. Mais curta do que a rotação: o texto acaba de
     aparecer antes de o cartão assentar no centro. */
  --afc-message-duration: 160ms;
  /* Abertura do leque à entrada no viewport: percurso muito maior do que o de
     uma rotação, por isso muito mais lenta. O valor real vem do script (ver
     ENTRY_DURATION_MS); este é só o encosto para o caso de não chegar. */
  --afc-entry-duration: 900ms;
  /* Sobe os controlos e o Module Message para dentro do poço do leque. O poço
     é fundo por construção — a pista reserva a altura do arco inteiro, mas por
     baixo do cartão central sobra tudo o que os cartões das pontas descem —,
     por isso abaixo de Desktop Lg fica a zero: aí o arco é pequeno e o vazio
     não chega para justificar a subida. */
  --afc-nav-pull: 0px;
  --afc-pad-y: var(--size-8); /* 48px */
  /* Margem da grelha, para a largura do Module Message. */
  --afc-margin: 24px;
  --afc-gutter: 24px;
  /* Fundo do módulo. O valor real vem da variante (--white/--solarized); este é
     o encosto para o caso de a classe da variante não chegar. */
  --afc-bg: #fff;
  /* Esbatimento dos cantos inferiores. `--afc-corner-fade-y` é a altura em que
     o fundo fecha por completo ao descer do arco; `--afc-corner-fade-x` é a
     faixa em que o mesmo fundo se desfaz a caminho do centro, para o corte
     junto ao cartão central não ser uma linha reta. */
  --afc-corner-fade-y: 85px;
  --afc-corner-fade-x: var(--size-10); /* 64px */

  @apply relative flex flex-col items-center w-full select-none;
  /* isolation: o z-index dos cartões, gradientes e controlos fica contido no
     módulo em vez de competir com o resto da página. */
  @apply isolate;
  overflow: clip;
  padding-block: var(--afc-pad-y);
  gap: var(--size-7); /* 40px, entre o arco+controlos e o Module Message */
  background-color: var(--afc-bg);
}

/* Os dois fundos do módulo. Os valores são os mesmos do AlertBanner — literais,
   porque os tokens de fundo do DS mudam de valor conforme o âmbito de tema em
   que o módulo cai e aqui a cor tem de ser a que os gradientes dos cantos
   também usam. */
.arc-focus-carousel--white {
  --afc-bg: #fff;
}

.arc-focus-carousel--solarized {
  --afc-bg: #f6f7f2;
}

@media (min-width: 1024px) {
  .arc-focus-carousel {
    --afc-arc-height: calc(630px * var(--afc-scale));
    --afc-pad-y: var(--size-11); /* 80px */
    --afc-margin: 32px;
  }
}

@media (min-width: 1366px) {
  .arc-focus-carousel {
    /* Desktop Lg: meio caminho entre os 182x288 pedidos pela DR e os 240x380 do
       protótipo. Aos 240x380 o leque cresce tanto que empurra o Module Message
       para fora do ecrã em portáteis de 1366; a 217x344 mantém o peso que o
       cartão perdia na versão reduzida sem obrigar a scroll. 217/240 e 344/380
       voltam a dar o mesmo fator, por isso continua a ser o leque inteiro à
       escala e não só o cartão. */
    --afc-scale: 0.9047;
    --afc-nav-pull: 80px;
    --afc-margin: 44px;
  }
}

@media (min-width: 1536px) {
  .arc-focus-carousel {
    --afc-margin: 128px;
  }
}

@media (min-width: 1920px) {
  .arc-focus-carousel {
    --afc-margin: 320px;
  }
}

/* O arco e os controlos formam um bloco: os controlos assentam no "poço" do
   leque, cuja profundidade é dada pela altura reservada ao arco. */
.arc-focus-carousel__stage {
  @apply relative flex flex-col items-center w-full;
}

/* A pista não roda: é aqui que o swipe é lido, para a área sensível não andar
   inclinada com o arco. Só reserva a altura — os cartões passam para fora. */
.arc-focus-carousel__track {
  @apply relative w-full shrink-0;
  height: var(--afc-arc-height);
  /* O gesto horizontal é nosso; o vertical continua a fazer scroll da página. */
  touch-action: pan-y;
}

/* A roda, mais larga do que o módulo: transborda simetricamente e é o
   `overflow: clip` do módulo que a recorta. */
.arc-focus-carousel__wheel {
  @apply absolute top-0 left-1/2;
  width: var(--afc-arc-width);
  height: var(--afc-arc-height);
  margin-left: calc(var(--afc-arc-width) / -2);
  transform-origin: 50% var(--afc-wheel-pivot);
  transition: transform var(--afc-rotation-duration) var(--afc-rotation-ease);
  will-change: transform;
}

/* Posição do cartão na roda. Depois da entrada o ângulo de um cartão montado
   nunca muda — quem roda é a roda —, pelo que a transição do transform só chega
   a correr uma vez: quando o leque se abre. */
.arc-focus-carousel__slot {
  @apply absolute left-1/2 cursor-pointer;
  top: var(--afc-card-top);
  width: var(--afc-card-width);
  height: var(--afc-card-height);
  margin-left: calc(var(--afc-card-width) / -2);
  border-radius: var(--afc-card-radius);
  transform-origin: 50% var(--afc-card-pivot);
  transition:
    transform var(--afc-entry-duration) var(--afc-rotation-ease),
    opacity var(--afc-rotation-duration) var(--afc-rotation-ease);
  outline: none;
}

/* Focus — anel visível apenas via teclado. */
/* Azul do token de foco do DS, não o verde do tema: o anel de foco tem de se
   distinguir da marca para se ler como estado do sistema. */
.arc-focus-carousel__slot:focus-visible {
  outline: 2px solid var(--ca-color-outline-focus-only, #0a8bff);
  outline-offset: 2px;
}

.arc-focus-carousel__card {
  @apply w-full h-full overflow-hidden;
  border-radius: var(--afc-card-radius);
  opacity: var(--afc-card-opacity, 1);
  transition: opacity var(--afc-rotation-duration) var(--afc-rotation-ease);
}

/* Só o cartão central mostra a arte a cheio; os restantes ficam mais apagados,
   que é o que os afunda no fundo e deixa o foco no centro do arco. */
.arc-focus-carousel__slot:not(.is-active) .arc-focus-carousel__card {
  --afc-card-opacity: var(--afc-inactive-opacity);
}

/* O shimmer não é conteúdo: aparece igual em todos os cartões. */
.arc-focus-carousel--loading .arc-focus-carousel__card {
  --afc-card-opacity: 1;
}

.arc-focus-carousel__img {
  @apply w-full h-full object-cover pointer-events-none;
  border-radius: var(--afc-card-radius);
}

/* Acima dos cartões e dos gradientes: os controlos ficam no poço do arco. */
.arc-focus-carousel__nav {
  @apply relative z-30 flex items-center justify-center shrink-0;
  /* A margem negativa encurta a pista, por isso sobe os controlos e, com eles,
     tudo o que vem a seguir no módulo — o Module Message incluído. */
  margin-top: calc(-1 * var(--afc-nav-pull));
}

/* 48x48 com o ícone a 24x24, em px e não em rem: o módulo é montado por cima
   do site antigo, que traz o seu próprio font-size de raiz, e um `h-12` do
   Tailwind (3rem) deixaria o botão à mercê dele. */
.arc-focus-carousel__arrow {
  @apply flex items-center justify-center rounded-full transition-all shrink-0;
  width: 48px;
  height: 48px;
}

.arc-focus-carousel__arrow :deep(.svg-icon) {
  width: 24px;
  height: 24px;
}

.arc-focus-carousel__arrow :deep(.svg-icon svg) {
  width: 100%;
  height: 100%;
}

.arc-focus-carousel__arrow:disabled {
  @apply opacity-40 cursor-not-allowed;
}

.arc-focus-carousel__arrow:not(:disabled):hover {
  @apply opacity-90;
}

/* Desvanece as pontas dos cartões exteriores contra o fundo, nos dois cantos
   inferiores do módulo. Começa onde o arco acaba e desce até ao fim: o
   esbatimento vertical são os primeiros `--afc-corner-fade-y`, o resto é fundo
   cheio.

   O esbatimento é feito por máscara e não por gradiente de cor: assim a única
   cor nomeada é a do fundo do módulo (`--afc-bg`), e o desvanecer é sempre
   exatamente para ela.

   Duas máscaras, em dois elementos: a vertical fica no `__fade` e a horizontal
   no `::before` que pinta o fundo. Estando uma dentro da outra multiplicam-se —
   é o que se quer, o canto só é opaco onde ambas o são — sem depender de
   `mask-composite`, que os browsers só ganharam há pouco. */
.arc-focus-carousel__fade {
  @apply absolute z-20 hidden pointer-events-none;
  width: 400px;
  top: calc(var(--afc-pad-y) + var(--afc-arc-height));
  bottom: 0;
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0,
    #fff var(--afc-corner-fade-y)
  );
  mask-image: linear-gradient(
    to bottom,
    transparent 0,
    #fff var(--afc-corner-fade-y)
  );
}

/* O fundo do canto. Vive num pseudo-elemento para poder levar a máscara
   horizontal por cima da vertical do pai. */
.arc-focus-carousel__fade::before {
  content: "";
  @apply absolute inset-0;
  background-color: var(--afc-bg);
}

.arc-focus-carousel__fade--left {
  left: 0;
}

.arc-focus-carousel__fade--left::before {
  -webkit-mask-image: linear-gradient(
    to right,
    #fff calc(100% - var(--afc-corner-fade-x)),
    transparent
  );
  mask-image: linear-gradient(
    to right,
    #fff calc(100% - var(--afc-corner-fade-x)),
    transparent
  );
}

.arc-focus-carousel__fade--right {
  right: 0;
}

.arc-focus-carousel__fade--right::before {
  -webkit-mask-image: linear-gradient(
    to left,
    #fff calc(100% - var(--afc-corner-fade-x)),
    transparent
  );
  mask-image: linear-gradient(
    to left,
    #fff calc(100% - var(--afc-corner-fade-x)),
    transparent
  );
}

/* Abaixo de 1024px os cartões exteriores já estão fora do ecrã — não há pontas
   para esconder e o gradiente só comeria o Module Message. */
@media (min-width: 1024px) {
  .arc-focus-carousel__fade {
    @apply block;
  }
}

/* Module Message — mesma estrutura do componente do design system. Em mobile e
   tablet ocupa a grelha toda; a partir de 1024px assenta em 8 colunas. */
.arc-focus-carousel__message {
  @apply relative z-30 flex flex-col items-center text-center shrink-0;
  width: calc(100% - 2 * var(--afc-margin));
  /* O bloco é recriado a cada mudança de cartão (`key` no template), por isso
     a animação recomeça sozinha — nada a agendar, nada a cancelar. */
  animation: afc-message-in var(--afc-message-duration) ease both;
}

@keyframes afc-message-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@media (min-width: 1024px) {
  .arc-focus-carousel__message {
    width: calc(
      (100% - 2 * var(--afc-margin) + var(--afc-gutter)) * 8 / 12 -
        var(--afc-gutter)
    );
    max-width: 845px;
  }
}

.arc-focus-carousel__message-block {
  @apply flex flex-col items-center w-full;
}

/* Legendas longas não podem alargar o módulo. Nada as limitava: o wrapper do
   CTA é um item de um flex column com `align-items: center`, por isso mede-se
   pelo conteúdo, e prender só o wrapper não chega — um <button> dimensiona-se
   ao conteúdo mesmo sendo `display: flex`, e ignora a largura do pai.

   Presa a largura, a legenda tem de ir a algum lado. O design system corta-a
   com reticências (`nowrap` + `text-overflow: ellipsis` no `.button-text`), mas
   num CTA isso esconde justamente o que se quer ler, por isso aqui ela quebra
   para baixo e o botão cresce. `anywhere` cobre o caso de a legenda vir sem
   espaços, em que uma palavra só voltaria a rebentar com a caixa. */
.arc-focus-carousel__cta {
  max-width: 100%;
}

.arc-focus-carousel__cta :deep(.base-button) {
  max-width: 100%;
  /* O design system fixa 48px de altura à volta de uma linha de 24px com 1px de
     borda, o que deixa exatamente 11px acima e abaixo. Repô-los como padding a
     sério, com os 48px a passarem a mínimo, deixa uma linha na mesma altura de
     sempre e faz cada linha extra somar os seus 24px: 48 → 72 → 96. */
  height: auto;
  min-height: 48px;
  padding-block: 11px;
}

.arc-focus-carousel__cta :deep(.button-text) {
  white-space: normal;
  overflow: visible;
  overflow-wrap: anywhere;
}

/* ── Loading (shimmer) ──────────────────────────────────────────────────
   Só as dimensões: o brilho vem do `.gradient-loading` do design system. */

.arc-focus-carousel__skeleton-title {
  @apply w-full;
  height: 40px;
  border-radius: var(--size-3);
}

.arc-focus-carousel__skeleton-text {
  @apply w-full;
  height: 24px;
  border-radius: var(--size-3);
}

.arc-focus-carousel__skeleton-button {
  width: 91px;
  height: 48px;
  border-radius: var(--size-3);
}

@media (min-width: 1024px) {
  .arc-focus-carousel__skeleton-title {
    height: 60px;
    border-radius: var(--size-4);
  }

  .arc-focus-carousel__skeleton-text {
    height: 26px;
  }
}

/* O leque em shimmer não é interativo. */
.arc-focus-carousel--loading .arc-focus-carousel__slot {
  @apply cursor-default;
}

/* Anúncios para leitores de ecrã, sem ocupar espaço no layout. */
.arc-focus-carousel__sr-only {
  @apply absolute w-px h-px p-0 m-0 overflow-hidden whitespace-nowrap border-0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
}

@media (prefers-reduced-motion: reduce) {
  .arc-focus-carousel__wheel,
  .arc-focus-carousel__slot,
  .arc-focus-carousel__card {
    transition: none;
  }

  /* O texto troca de conteúdo sem aparecer a fazer-se. */
  .arc-focus-carousel__message {
    animation: none;
  }

  /* O shimmer do design system também é movimento. */
  .arc-focus-carousel :deep(.gradient-loading) {
    animation: none;
  }
}
</style>
